import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CustomInput from "../../components/common/CustomInput";
import CustomTextArea from "../../components/common/CustomTextArea";
import CustomImageUploader from "../../components/common/CustomImageUploader";
import axios from "axios";
import config from "../../config";

const initialFormData = {
  title: "",
  description: "",
  coverImage: null,
  albumImages: [],
};

const AddAlbumModal = ({ open, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const modalContentRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);

  // Reset form data when modal opens
  useEffect(() => {
    if (open) {
      setFormData(initialFormData);
      setActiveTab(0);
    }
  }, [open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCoverImageUpload = (newImages) => {
    // Chỉ lấy ảnh cuối cùng làm ảnh bìa
    setFormData((prev) => ({ 
      ...prev, 
      coverImage: newImages.length > 0 ? newImages[newImages.length - 1] : null 
    }));
  };

  const handleAlbumImagesUpload = (newImages) => {
    // Lưu vị trí scroll hiện tại trước khi cập nhật state
    const scrollPosition = modalContentRef.current?.scrollTop || 0;
    
    // Cập nhật state với tất cả ảnh
    setFormData((prev) => ({ ...prev, albumImages: newImages }));
    
    // Đảm bảo scroll position được khôi phục sau khi re-render
    requestAnimationFrame(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = scrollPosition;
      }
    });
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert("❌ Tiêu đề album là bắt buộc.");
      return;
    }

    if (!formData.coverImage || !formData.coverImage.file) {
      alert("❌ Vui lòng chọn ảnh bìa cho album.");
      return;
    }

    const albumImagesCount = formData.albumImages.filter(img => img.file).length;
    if (albumImagesCount === 0) {
      alert("❌ Vui lòng chọn ít nhất 1 hình ảnh cho album.");
      return;
    }

    setLoading(true);
    try {
      // Bước 1: Tạo album với thông tin cơ bản và ảnh bìa
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description);
      
      // Thêm ảnh bìa
      if (formData.coverImage && formData.coverImage.file) {
        formDataToSend.append("coverImage", formData.coverImage.file);
      }
      
      // Thêm ảnh bìa URL nếu có
      if (formData.coverImage && formData.coverImage.preview) {
        formDataToSend.append("coverImageUrl", formData.coverImage.preview);
      }

      const response = await axios.post(
        `${config.apiBaseUrl}/Album/create`, 
        formDataToSend, 
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 200 && response.data) {
        // Bước 2: Thêm ảnh vào album đã tạo
        const albumId = response.data.albumId || response.data;
        
        // Tạo FormData mới cho việc thêm ảnh
        const albumImagesFormData = new FormData();
        
        // Thêm albumId vào request
        albumImagesFormData.append("albumId", albumId);
        
        // Thêm tất cả ảnh trong album
        formData.albumImages.forEach((image) => {
          if (image.file) {
            albumImagesFormData.append("imageFiles", image.file);
          }
        });
        
        // Gọi API thêm ảnh vào album
        const addImagesResponse = await axios.post(
          `${config.apiBaseUrl}/Album/add-images`, 
          albumImagesFormData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        
        if (addImagesResponse.status === 200) {
          alert(`✅ Album đã được thêm thành công!`);
          onSave?.();
          onClose();
        } else {
          alert("❌ Có lỗi xảy ra khi thêm ảnh vào album.");
        }
      } else {
        alert("❌ Có lỗi xảy ra khi thêm album.");
      }
    } catch (error) {
      console.error("❌ Lỗi khi gửi yêu cầu:", error);
      if (error.response) {
        alert(`❌ Lỗi API: ${error.response.status} - ${error.response.data?.Message || "Không xác định"}`);
      } else {
        alert("❌ Không thể kết nối đến server!");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Modal open={open} onClose={handleClose} keepMounted>
      <Box sx={{ 
        position: "absolute", 
        top: "50%", 
        left: "50%", 
        transform: "translate(-50%, -50%)", 
        width: "90%", 
        maxWidth: "900px", 
        bgcolor: "background.paper", 
        boxShadow: 10, 
        p: 3, 
        borderRadius: 2, 
        display: "flex", 
        flexDirection: "column", 
        maxHeight: "85vh", 
        overflow: "hidden" 
      }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}>
          Thêm Album Mới
        </Typography>
        
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          centered
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Thông tin Album" />
          <Tab label="Thêm Ảnh" />
        </Tabs>
        
        <Box 
          ref={modalContentRef}
          sx={{ 
            flexGrow: 1, 
            overflowY: "auto", 
            p: 2,
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": {
              width: "8px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "4px"
            }
          }}
        >
          {activeTab === 0 && (
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: "#f9f9f9" }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}>
                Thông tin Album
              </Typography>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 4 }}>
                    <CustomInput 
                      label="Tiêu đề" 
                      name="title" 
                      value={formData.title} 
                      onChange={handleInputChange} 
                      required 
                      fullWidth
                    />
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Mô tả chi tiết
                    </Typography>
                    <CustomTextArea 
                      initialContent={formData.description} 
                      onChange={(val) => setFormData({ ...formData, description: val })} 
                      style={{ 
                        minHeight: "150px",
                        height: "150px",
                        width: "100%",
                        maxWidth: "100%"
                      }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Ảnh bìa album
                    </Typography>
                    <CustomImageUploader 
                      onUpload={handleCoverImageUpload} 
                      value={formData.coverImage ? [formData.coverImage] : []} 
                      onChange={handleCoverImageUpload} 
                      style={{
                        minHeight: "200px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                      maxImages={1}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                      Ảnh bìa sẽ hiển thị ở trang chính của album
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
          
          {activeTab === 1 && (
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: "#f9f9f9" }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}>
                Thêm Ảnh Vào Album
              </Typography>
              
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Hình ảnh trong album
                </Typography>
                <CustomImageUploader 
                  onUpload={handleAlbumImagesUpload} 
                  value={formData.albumImages} 
                  onChange={handleAlbumImagesUpload} 
                  style={{
                    minHeight: "300px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Bạn có thể thêm nhiều ảnh vào album
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>
        
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, p: 1, borderTop: "1px solid #ddd", gap: 2 }}>
          {activeTab === 0 ? (
            <>
              <Button 
                variant="contained" 
                color="primary" 
                sx={{ 
                  width: "40%", 
                  fontWeight: "bold",
                  textTransform: "uppercase"
                }} 
                onClick={() => setActiveTab(1)}
              >
                Tiếp Theo
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                sx={{ 
                  width: "40%", 
                  fontWeight: "bold",
                  textTransform: "uppercase"
                }} 
                onClick={handleClose}
              >
                Hủy
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="contained" 
                color="primary" 
                sx={{ 
                  width: "40%", 
                  fontWeight: "bold",
                  textTransform: "uppercase"
                }} 
                onClick={handleSave} 
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Lưu Album"}
              </Button>
              <Button 
                variant="outlined" 
                color="primary" 
                sx={{ 
                  width: "40%", 
                  fontWeight: "bold",
                  textTransform: "uppercase"
                }} 
                onClick={() => setActiveTab(0)}
              >
                Quay Lại
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default AddAlbumModal;

















